import { z } from 'zod';
import { isoDateSchema } from './common.js';

/**
 * Wave 15.1 — DRE por obra (per-project income statement).
 *
 * DRE = Demonstração do Resultado do Exercício. In a construction
 * context this is the project-level P&L that a PM / PGM reviews with
 * finance once a month or on demand:
 *
 *   RECEITA           = medições aprovadas + aditivos aprovados
 *   CUSTOS            = POs emitidas + despesas diretas − estornos
 *   LUCRO BRUTO       = RECEITA − CUSTOS
 *   MARGEM %          = LUCRO BRUTO / RECEITA
 *
 * The API returns two views:
 *  • accrual  — what's been earned/committed (source of truth for margin)
 *  • cash     — what's actually moved through the bank account
 *
 * Both share the same period filter. Period is optional; when omitted
 * the endpoint returns the all-time cumulative DRE (since project
 * start). When `from`/`to` are supplied they act as [from, to] inclusive
 * bounds on the relevant date field for each line (measurements:
 * period_end, change_orders: decided_at, purchase_orders: issued_at,
 * payments: occurred_at).
 */

/** `YYYY-MM-DD` bounds for the DRE window. Both sides optional. */
export const dreQuerySchema = z
  .object({
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
  })
  .refine(
    (v) => !v.from || !v.to || v.from <= v.to,
    { message: 'from must be on or before to' },
  );
export type DreQuery = z.infer<typeof dreQuerySchema>;

export const dreProjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  clientName: z.string(),
  /** Baseline contract value entered when the project was created. */
  budgetCents: z.number().int().nonnegative().nullable(),
});
export type DreProject = z.infer<typeof dreProjectSchema>;

/** The revenue side — what the client contractually owes. */
export const dreRevenueSchema = z.object({
  /** Sum of approved/sent/paid measurements in the window. */
  measurementsApprovedCents: z.number().int().nonnegative(),
  /** Sum of approved change_orders.delta_cents (signed — can be negative). */
  changeOrdersApprovedCents: z.number().int(),
  /** Row count per bucket, rendered as badges in the UI. */
  measurementsCount: z.number().int().nonnegative(),
  changeOrdersCount: z.number().int().nonnegative(),
  /** measurementsApprovedCents + changeOrdersApprovedCents. */
  totalCents: z.number().int(),
});
export type DreRevenue = z.infer<typeof dreRevenueSchema>;

/** The cost side — committed and paid spend. */
export const dreCostsSchema = z.object({
  /**
   * Sum of purchase_orders.total_cents where status != 'cancelled'.
   * This is "committed spend": we've issued the PO even if nothing has
   * been received/paid yet.
   */
  purchaseOrdersCents: z.number().int().nonnegative(),
  purchaseOrdersCount: z.number().int().nonnegative(),
  /**
   * Settled payments with kind IN ('reimbursement','other') —
   * operating expenses that didn't flow through a PO.
   */
  otherExpensesCents: z.number().int().nonnegative(),
  otherExpensesCount: z.number().int().nonnegative(),
  /** Settled payments with kind='refund' — reduces total cost. */
  refundsCents: z.number().int().nonnegative(),
  refundsCount: z.number().int().nonnegative(),
  /** purchaseOrdersCents + otherExpensesCents − refundsCents. */
  totalCents: z.number().int(),
});
export type DreCosts = z.infer<typeof dreCostsSchema>;

export const dreResultSchema = z.object({
  grossProfitCents: z.number().int(),
  /** 0–100, rounded to 2 decimals. 0 when revenue is zero. */
  marginPercent: z.number(),
});
export type DreResult = z.infer<typeof dreResultSchema>;

/** Cash-basis view — what actually moved through the bank. */
export const dreCashFlowSchema = z.object({
  /** Settled payments with kind='measurement' — money in from the client. */
  receivedFromClientCents: z.number().int().nonnegative(),
  /** Settled payments with kind='po' — money out to suppliers. */
  paidToSuppliersCents: z.number().int().nonnegative(),
  /** Settled payments with kind IN ('reimbursement','other') — other outflows. */
  otherOutflowsCents: z.number().int().nonnegative(),
  /** Settled payments with kind='refund'. */
  refundsCents: z.number().int().nonnegative(),
  /** received − (paid + other − refunds). Signed. */
  netCents: z.number().int(),
});
export type DreCashFlow = z.infer<typeof dreCashFlowSchema>;

export const dreReportSchema = z.object({
  project: dreProjectSchema,
  period: z.object({
    from: isoDateSchema.nullable(),
    to: isoDateSchema.nullable(),
  }),
  revenue: dreRevenueSchema,
  costs: dreCostsSchema,
  result: dreResultSchema,
  cashFlow: dreCashFlowSchema,
  generatedAt: z.string(),
});
export type DreReport = z.infer<typeof dreReportSchema>;
